import { useMemo, useRef, useState } from 'react';

/**
 * Data filtering hook
 *
 * @param {array} data The data to filter on
 * @param {function} filterFn The (factory) function to filter the data with
 *
 * @example
 *  const { data, setFilterTerm, filterTerm } = useFilter<Analysis>(
 *    analyses,
 *    term => (item): boolean =>
 *      item.name.toLowerCase().includes(term) ||
 *      item.tags.some((tag: string) => tag.toLowerCase().includes(term))
 *  );
 */
const useFilter = <T>(
  data: Array<T>,
  filterFn: (term: string) => (item: T) => boolean
): { data: Array<T>; filterTerm: string; setFilterTerm: (term: string) => void } => {
  const [filterTerm, setFilterTerm] = useState('');
  const filterFnRef = useRef<(term: string) => (item: T) => boolean>(filterFn);
  filterFnRef.current = filterFn;
  const filter = useMemo(() => filterFnRef.current(filterTerm), [filterFnRef, filterTerm]);

  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return data;
    }
    return data.filter(filter);
  }, [data, filter]);

  return { data: filteredData, filterTerm, setFilterTerm };
};

export default useFilter;
